/*
 * The live per-stage/per-ticket status feed, streamed over the run's WebSocket.
 * Renders the connection state (a pill) plus the most-recent frames newest-last,
 * each `{ stage, status }` rendered with the shared run StatusBadge so the live
 * feed reads in the same visual language as the rest of the console.
 *
 * Driven by `useRunStatus(runId)` in the page; this component is presentational
 * over the `{ history, latest, connection }` it returns.
 */

import type { RunStatusFrame } from "../../ws/useRunStatus";
import type { SocketStatus } from "../../ws/ReconnectingSocket";
import { StatusPill } from "../StatusPill";
import styles from "./pipeline.module.css";

const CONN_LABEL: Record<SocketStatus, string> = {
  connecting: "Connecting…",
  open: "Live",
  reconnecting: "Reconnecting…",
  closed: "Offline",
};

const CONN_CLASS: Record<SocketStatus, string> = {
  connecting: styles.connConnecting,
  open: styles.connOpen,
  reconnecting: styles.connReconnecting,
  closed: styles.connClosed,
};

export interface LiveStatusStreamProps {
  history: RunStatusFrame[];
  latest: RunStatusFrame | null;
  connection: SocketStatus;
}

export function ConnectionPill({ connection }: { connection: SocketStatus }) {
  return (
    <span
      className={`${styles.conn} ${CONN_CLASS[connection]}`}
      role="status"
      aria-live="polite"
    >
      <span className={styles.connDot} aria-hidden="true" />
      {CONN_LABEL[connection]}
    </span>
  );
}

export function LiveStatusStream({
  history,
  latest,
  connection,
}: LiveStatusStreamProps) {
  // Newest first reads best in an operator feed; the latest frame is the head.
  const frames = [...history].reverse();

  return (
    <section className={styles.panel} aria-label="Live status stream">
      <div className={styles.panelHeader}>
        <h2 className={styles.panelTitle}>Live status</h2>
        <ConnectionPill connection={connection} />
      </div>

      {frames.length === 0 ? (
        <p className={styles.empty}>
          {connection === "open"
            ? "Connected — waiting for the first status frame…"
            : "No live frames yet."}
        </p>
      ) : (
        <ul className={styles.feed}>
          {frames.map((frame, i) => {
            const isLatest = latest != null && i === 0;
            return (
              <li
                // Frames have no id; index within the bounded reversed list is a
                // stable enough key for a render-only feed.
                key={`${frame.stage}-${frame.status}-${i}`}
                className={`${styles.frame} ${isLatest ? styles.frameLatest : ""}`}
              >
                <span className={styles.frameStage} title={frame.stage}>
                  {frame.stage || "—"}
                </span>
                <StatusPill kind="run" value={frame.status} hideDot />
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
